import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon,
} from '@heroicons/react/24/outline';
import styles from './SyncHealthPage.module.css';

/**
 * Whether the syncs are actually working.
 *
 * The Business Central reconcile silently rolled back three paid renewals and
 * nothing surfaced it — the job discarded the stats it was handed, and sixteen
 * of forty-seven memberships had never reached BC at all, which means those
 * cards would not have worked at a till. None of that was visible anywhere.
 */

const JOB_LABELS = {
  bc_members:     'BC member directory',
  bc_vip:         'BC membership reconcile',
  bc_items:       'BC item catalogue',
  bc_push_retry:  'Retry failed BC pushes',
  shopify_orders: 'Shopify orders',
  payment_sweep:  'Unsettled payments',
};

const JOB_NOTES = {
  bc_members:    'Mirrors BC’s customer list so the admin can search it without calling BC.',
  bc_vip:        'Reconciles app memberships against BC. Runs every 3 hours.',
  bc_items:      'Refreshes the product catalogue behind the price scanner.',
  bc_push_retry: 'Pushes memberships that never reached BC. Runs hourly.',
  payment_sweep: 'Asks the gateway about payments whose result never arrived, so nobody is charged without getting their card. Runs hourly.',
};

export default function SyncHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminAPI.getSyncHealth()
      .then((res) => { setData(res); setError(null); })
      .catch((e) => setError(e?.message || 'Could not load sync health.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    // Refreshed rather than left stale: this page is opened precisely when
    // someone suspects something is wrong.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const m = data?.memberships;

  // What matters is not whether a job ran, but whether members are left
  // stranded — a job can succeed and still leave cards that do not work.
  const problems = [];
  if (m?.neverSyncedToBC > 0) {
    problems.push({
      level: 'bad',
      text: `${m.neverSyncedToBC} membership${m.neverSyncedToBC === 1 ? '' : 's'} never reached Business Central`,
      why: 'These members have paid but their card will not work at the till. The hourly retry should clear this.',
    });
  }
  if (m?.notVipInBC > 0) {
    problems.push({
      level: 'warn',
      text: `${m.notVipInBC} active member${m.notVipInBC === 1 ? ' is' : 's are'} not VIP in Business Central`,
      why: 'Their app shows a VIP card but the till would charge full price. Can also be a stale mirror — it refreshes every 3 hours.',
    });
  }
  if (m?.staleOver24h > 0) {
    problems.push({
      level: 'warn',
      text: `${m.staleOver24h} membership${m.staleOver24h === 1 ? '' : 's'} not synced in over 24 hours`,
      why: 'The reconcile may not be running.',
    });
  }

  const failedJobs = (data?.jobs || []).filter((j) => !j.ok);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>Sync Health</h1>
          <p className={styles.sub}>
            Business Central and Shopify synchronisation, and whether any member is stranded.
          </p>
        </div>
        <button className="btn-ghost" onClick={load} disabled={loading}>
          <ArrowPathIcon className={styles.btnIcon} />
          {loading ? 'Checking…' : 'Refresh'}
        </button>
      </div>

      {error && <div className={styles.errorBox}>{error}</div>}

      {/* The headline: is anyone actually affected right now. */}
      {!loading && !error && (
        problems.length === 0 ? (
          <div className={`${styles.banner} ${styles.bannerOk}`}>
            <CheckCircleIcon className={styles.bannerIcon} />
            <div>
              <p className={styles.bannerTitle}>Everything is in sync</p>
              <p className={styles.bannerBody}>
                All {m?.active ?? 0} active memberships have reached Business Central.
              </p>
            </div>
          </div>
        ) : (
          problems.map((p) => (
            <div
              key={p.text}
              className={`${styles.banner} ${p.level === 'bad' ? styles.bannerBad : styles.bannerWarn}`}
            >
              {p.level === 'bad'
                ? <XCircleIcon className={styles.bannerIcon} />
                : <ExclamationTriangleIcon className={styles.bannerIcon} />}
              <div>
                <p className={styles.bannerTitle}>{p.text}</p>
                <p className={styles.bannerBody}>{p.why}</p>
              </div>
            </div>
          ))
        )
      )}

      {/* Memberships */}
      <div className={styles.cards}>
        <Stat label="Active memberships" value={m?.active} />
        <Stat label="Never synced to BC" value={m?.neverSyncedToBC} bad={m?.neverSyncedToBC > 0} />
        <Stat label="Not VIP in BC" value={m?.notVipInBC} warn={m?.notVipInBC > 0} />
        <Stat label="Stale over 24h" value={m?.staleOver24h} warn={m?.staleOver24h > 0} />
      </div>

      {/* Jobs */}
      <h2 className={styles.sectionTitle}>Scheduled jobs</h2>
      {failedJobs.length > 0 && (
        <p className={styles.sectionNote}>
          {failedJobs.length} job{failedJobs.length === 1 ? '' : 's'} failed on the last run.
        </p>
      )}

      {loading && !data ? (
        <div className={styles.empty}>Loading…</div>
      ) : !data?.jobs?.length ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No runs recorded yet</p>
          <p className={styles.emptyBody}>
            Each job records itself the next time it runs. The BC reconcile runs every 3 hours.
          </p>
        </div>
      ) : (
        <div className={styles.jobs}>
          {data.jobs.map((j) => (
            <div key={j.job} className={styles.job}>
              <div className={styles.jobHead}>
                <div className={styles.jobName}>
                  <span className={`${styles.dot} ${j.ok ? styles.dotOk : styles.dotBad}`} />
                  <div>
                    <p className={styles.jobLabel}>{JOB_LABELS[j.job] || j.job}</p>
                    {JOB_NOTES[j.job] && <p className={styles.jobNote}>{JOB_NOTES[j.job]}</p>}
                  </div>
                </div>
                <span className={`badge ${j.ok ? 'badge-active' : 'badge-inactive'}`}>
                  {j.ok ? 'OK' : 'Failed'}
                </span>
              </div>

              <div className={styles.jobMeta}>
                <span>
                  Last run{' '}
                  {j.lastRun ? `${formatDistanceToNow(new Date(j.lastRun))} ago` : '—'}
                  {j.lastRun && ` · ${format(new Date(j.lastRun), 'dd MMM HH:mm')}`}
                </span>
                {j.durationMs != null && <span>{(j.durationMs / 1000).toFixed(1)}s</span>}
                <span>
                  {j.runsThisWeek} run{j.runsThisWeek === 1 ? '' : 's'} this week
                  {j.failuresThisWeek > 0 && `, ${j.failuresThisWeek} failed`}
                </span>
              </div>

              {j.error && <p className={styles.jobError}>{j.error}</p>}

              {j.stats && Object.keys(j.stats).length > 0 && (
                <div className={styles.stats}>
                  {Object.entries(j.stats)
                    .filter(([k, v]) => typeof v === 'number' && v !== 0)
                    .map(([k, v]) => (
                      <span key={k} className={styles.statChip}>
                        {k.replace(/([A-Z])/g, ' $1').toLowerCase()}: <b>{v}</b>
                      </span>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, bad, warn }) {
  return (
    <div className={`${styles.stat} ${bad ? styles.statBad : warn ? styles.statWarn : ''}`}>
      <p className={styles.statValue}>{value ?? '—'}</p>
      <p className={styles.statLabel}>{label}</p>
    </div>
  );
}
